import React from 'react';

/**
 * Footer de paginación reutilizado por GenericTables y por vistas que
 * paginan sin usar una tabla (ej. FileExplorerGrid) — misma UI en ambos casos.
 *
 * @param {Object} props
 * @param {Object} props.pagination - ver GenericTables para la forma exacta
 * @param {boolean} props.loading
 * @param {boolean} props.showFooter
 */
const PaginationFooter = ({ pagination = null, loading = false, showFooter = false }) => {
  if (!showFooter || !pagination) return null;

  const {
    currentPage,
    totalPages,
    pageSize,
    totalItems,
    pageSizeOptions = [5, 10, 20],
    onPageChange,
    onPageSizeChange
  } = pagination;

  const renderPaginationButtons = () => {
    const pages = [];
    const total = totalPages;

    if (total <= 5) {
      for (let p = 1; p <= total; p++) pages.push(p);
    } else {
      const middlePage = currentPage === 1 || currentPage === total
        ? Math.ceil(total / 2)
        : currentPage;

      pages.push(1);
      if (middlePage > 2) pages.push('left-ellipsis');
      if (middlePage > 1 && middlePage < total) pages.push(middlePage);
      if (middlePage < total - 1) pages.push('right-ellipsis');
      pages.push(total);
    }

    return pages.map((p, idx) => {
      if (p === 'left-ellipsis' || p === 'right-ellipsis') {
        return (
          <span key={`ell-${idx}`} className="px-3 py-1 text-content-muted">
            ...
          </span>
        );
      }

      return (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          disabled={loading}
          className={`px-3 py-1 rounded ${
            p === currentPage
              ? 'bg-[--app-sidebar-active] text-accent-text'
              : 'bg-surface-2 text-content'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {p}
        </button>
      );
    });
  };

  const hasFewPages = totalPages <= 5;

  return (
    <div className="mt-4 w-full max-w-full px-3 py-3 bg-bg border-t border-line flex flex-col gap-3 md:mt-0 md:px-4 md:flex-row md:items-center md:justify-between">
      <div className="flex w-full items-center justify-between md:w-auto">
        <div className="text-sm text-content">
          Total: <span className="font-medium">{totalItems}</span>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <div className="text-sm text-content whitespace-nowrap">
            Pagina {currentPage} de {totalPages}
          </div>

          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={loading}
            className="px-2 py-1 bg-surface text-content border border-line rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:gap-3 md:justify-end">
        <div className="hidden md:block">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={loading}
            className="px-2 py-1 bg-surface text-content border border-line rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className={`flex w-full items-center gap-1 ${hasFewPages ? 'justify-center' : 'justify-between'} md:w-auto md:justify-start`}>
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={loading || currentPage <= 1}
            className="px-3 py-1 rounded bg-surface-2 text-content disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ◀
          </button>

          {renderPaginationButtons()}

          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={loading || currentPage >= totalPages}
            className="px-3 py-1 rounded bg-surface-2 text-content disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ▶
          </button>
        </div>

        <div className="hidden text-sm text-content whitespace-nowrap md:block">
          Pagina {currentPage} de {totalPages}
        </div>
      </div>
    </div>
  );
};

export default PaginationFooter;
